import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Icon, IconButton } from '@cbrebuild/blocks';
import ConfirmationModal from '../../../nucleus/confirmation-modal/confirmation-modal';
import dealsService from '../../../services/deals-service';
import userEventService from '../../../services/user-event-service';
import { postAttachmentsToExtract, startDealExtractionPolling, subscribeToDealExtractionStatus } from '../../../services/attachments-service';
import { dispatchLeaseExtractionRefreshRequest } from '../../../hooks/useLeaseExtractionPersistedDataRefresh';

const SparkleGlyph = () => (
  <svg className="voucher-sparkle-svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 2 L10.6 6.8 L15.5 8.5 L10.6 10.2 L9 15 L7.4 10.2 L2.5 8.5 L7.4 6.8 Z" fill="currentColor" />
    <path d="M18 3 L18.9 5.6 L21.5 6.5 L18.9 7.4 L18 10 L17.1 7.4 L14.5 6.5 L17.1 5.6 Z" fill="currentColor" />
    <path d="M17.5 13.5 L18.2 15.6 L20.3 16.3 L18.2 17 L17.5 19.1 L16.8 17 L14.7 16.3 L16.8 15.6 Z" fill="currentColor" />
  </svg>
);

class VoucherFileListItem extends React.Component {
  // eslint-disable-next-line react/sort-comp
  container = React.createRef();
  unsubscribeExtractionStatus = null;
  lastRefreshKey = null;
  state = {
    open: false,
    showModal: false,
    extractionStatus: null,
    retrying: false,
  }

  closeConfirmationModal = () => {
    this.setState({ showModal: false });
  }

  handleDeleteFile = (id) => {
    const { deleteFile, file } = this.props;
    deleteFile(id);

    dealsService.updateDeal(file.deal, {
      modified: new Date(),
    });
  }

  trackDownloadFile = () => {
    const {
      analyticProperties: {
        actionPrefix,
        categoryPrefix,
      },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: 'file_downloaded',
        eventCategory: 'File Action',
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  }

  handleFileDownload = (id) => {
    const { fetchFilesDownloadLink } = this.props;
    fetchFilesDownloadLink(id, true);
    this.trackDownloadFile();
  }

  addToVoucher = () => {
    const {
      file,
      updateFile,
    } = this.props;
    const params = {
      ...file,
      is_lease_document: true,
    };

    updateFile(file.id, params);
    dealsService.updateDeal(file.deal, {
      modified: new Date(),
    });
  }

  removeFromVoucher = () => {
    const {
      file,
      updateFile,
      onRemoveFromVoucher,
    } = this.props;
    const params = {
      ...file,
      is_lease_document: false,
    };

    dealsService.updateDeal(file.deal, {
      modified: new Date(),
    });
    return Promise.resolve(updateFile(file.id, params)).then((result) => {
      if (onRemoveFromVoucher) {
        onRemoveFromVoucher(file.id);
      }
      return result;
    });
  }

  showConfirmationModal = () => {
    this.setState({ showModal: true });
    this.setState(state => ({
      open: !state.open,
    }));
  }
  toggleDropdown = () => {
    this.setState(state => ({
      open: !state.open,
    }));
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    this.subscribeToExtractionStatus();
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    if (this.unsubscribeExtractionStatus) {
      this.unsubscribeExtractionStatus();
    }
  }

  handleClickOutside = (event) => {
    if (this.container.current && !this.container.current.contains(event.target)) {
      this.setState({
        open: false,
      });
    }
  };

  subscribeToExtractionStatus = () => {
    const { file, dealType, showSparkleIcon } = this.props;
    const normalizedDealType = String(dealType || '').toLowerCase();
    // Subscribe for files with known extraction history, and for Getting Started
    // sparkle rows so we can fetch 'Stopped' and render the sparkle action.
    const shouldSubscribe = (normalizedDealType === 'lease' || showSparkleIcon) &&
      file.id && file.deal &&
      (file.extraction_opted_in || file.is_lease_document || showSparkleIcon);
    if (!shouldSubscribe) return;

    if (this.unsubscribeExtractionStatus) {
      this.unsubscribeExtractionStatus();
    }

    this.unsubscribeExtractionStatus = subscribeToDealExtractionStatus({
      dealId: file.deal,
      fileId: file.id,
      onUpdate: this.handleExtractionStatusUpdate,
    });
  };

  handleExtractionStatusUpdate = (result) => {
    const { file } = this.props;
    if (!result) {
      return;
    }

    this.setState({
      extractionStatus: result.status || null,
    });

    if (result.status === 'Success') {
      const refreshKey = `${file.id}:${result.id || result.status_text || result.status}`;
      if (refreshKey !== this.lastRefreshKey) {
        this.lastRefreshKey = refreshKey;
        dispatchLeaseExtractionRefreshRequest({
          dealId: file.deal,
          fileId: file.id,
          refreshKey,
          extractionResult: result,
        });
      }
    }
  };

  handleRetry = () => {
    const { file, updateFile } = this.props;

    this.setState({
      retrying: true,
      extractionStatus: 'Processing',
    });

    // Mirror post-upload extraction sequence: opt-in first, then trigger extraction, then poll by deal.
    Promise.resolve(updateFile(file.id, {
      ...file,
      extraction_opted_in: true,
    }))
      .then(() => postAttachmentsToExtract({
        deal: file.deal,
        file: file.id,
        attachment_file_key: null,
        is_abstract: true,
      }))
      .then(() => startDealExtractionPolling(file.deal, file.id, { initialDelayMs: 12000 }))
      .then(() => {
        this.setState({ retrying: false });
      })
      .catch(() => {
        this.setState({ retrying: false, extractionStatus: 'Failure' });
      });
  };

  shouldShowExtractionStatus = () => {
    const { dealType, file, showSparkleIcon } = this.props;
    const { retrying, extractionStatus } = this.state;
    const normalizedDealType = String(dealType || '').toLowerCase();

    if (showSparkleIcon) {
      return retrying || Boolean(extractionStatus);
    }

    return normalizedDealType === 'lease' && (
      file.is_lease_document ||
      file.extraction_opted_in ||
      retrying ||
      Boolean(extractionStatus)
    );
  };

  renderExtractionStatus = () => {
    const { extractionStatus, retrying } = this.state;

    if (extractionStatus === 'Failure' && !retrying) {
      return (
        <span className="voucher-lease-import-status voucher-lease-import-status--failed">
          Abstraction unavailable.{' '}
          <a
            href="#"
            className="retry-link"
            onClick={(e) => { e.preventDefault(); this.handleRetry(); }}
          >
            Retry
          </a>
          {' '}or continue by entering lease details manually.
        </span>
      );
    }
    if (extractionStatus === 'Processing' || extractionStatus === 'Queued' || retrying) {
      return (
        <span className="voucher-lease-import-status">Abstraction in progress&hellip;</span>
      );
    }
    if (extractionStatus === 'Stopped') {
      return (
        <span className="voucher-lease-import-status">
          Abstraction paused. If this is a Lease, click{' '}
          <span
            className="inline-sparkle-icon inline-sparkle-icon-clickable"
            onClick={!retrying ? this.handleRetry : undefined}
            role="button"
            tabIndex={retrying ? -1 : 0}
            onKeyDown={!retrying ? (e) => { if (e.key === 'Enter' || e.key === ' ') this.handleRetry(); } : undefined}
            aria-label="Import Data from Document"
            aria-disabled={retrying}
          >
            <SparkleGlyph />
          </span>
          {' '}icon to import data.
        </span>
      );
    }
    // Show 'complete' when extraction succeeded
    if (extractionStatus === 'Success') {
      return (
        <span className="voucher-lease-import-status">
          Lease import to voucher is complete.
        </span>
      );
    }
    return null;
  };

  render() {
    const {
      file,
      voucher,
      isAddButton,
      isDropDown,
      showSparkleIcon,
    } = this.props;
    const {
      showModal,
      retrying,
      extractionStatus,
    } = this.state;

    return (
      <React.Fragment>
        {(Object.keys(file).length !== 0) &&
          <li className="voucher-file-list-item" key={file.id}>
            <div className="file-icon-wrapper">
              <Icon iconName="document" size="x-large" />
              <div className={`doc-type-extension doc-type-${file.file_type}`} onClick={this.testClick}>{file.file_type}</div>
            </div>
            <div className="file-info">
              <a className="file-list-name" title={file.document_name}>{file.title}</a>
              <span className="file-metadata">
                {file.created && moment(file.created).format('ll')}
              </span>
              {this.shouldShowExtractionStatus() && (
                this.renderExtractionStatus()
              )}
            </div>
            <div className="file-actions">
              {showSparkleIcon && extractionStatus === 'Stopped' && (
                <span className="voucher-sparkle-tooltip" title="Import Data from Document">
                  <button
                    type="button"
                    className="voucher-sparkle-icon-button"
                    onClick={!retrying ? this.handleRetry : undefined}
                    disabled={retrying}
                    aria-label="Import Data from Document"
                  >
                    <span className="voucher-sparkle-icon" aria-hidden="true">
                      <SparkleGlyph />
                    </span>
                  </button>
                </span>
              )}
              {isAddButton ? <IconButton
                className={isAddButton ? 'add-voucher-icon' : 'download-voucher-icon'}
                iconName="plus-circle-outline"
                onClick={this.addToVoucher}
              /> : <IconButton
                className="download-voucher-icon"
                iconName="download"
                onClick={() => this.handleFileDownload(file.id)}
              />}
              {isDropDown &&
                <div className="dropdown-menu-voucher" ref={this.container}>
                  {isAddButton ? <span className="add-button" onClick={this.addToVoucher}>Add to Voucher</span> : <Icon
                    iconName="more-v"
                    onClick={this.toggleDropdown}
                    onBlur={this.toggleDropdown}
                  />}
                  {this.state.open && (
                    <div className="dropdown-list-voucher">
                      <ul>
                        <li onClick={this.removeFromVoucher}> Remove from {voucher.is_early_invoice ? 'Early Invoice' : 'Voucher'}</li>
                        <li onClick={this.showConfirmationModal}>Delete File</li>
                      </ul>
                    </div>
                  )}
                </div>
              }
            </div>
          </li>
        }
        {showModal &&
          <ConfirmationModal
            className="deal-file-delete-confirmation voucher-modal"
            onCloseModal={this.closeConfirmationModal}
            showModal={showModal}
          >
            <div className="header-modal">
              <h1 className="header-content conf-header">Confirmation</h1>
              <IconButton
                iconName="close-circle"
                className="icon-circle"
                variant="basic"
                onClick={this.closeConfirmationModal}
              />
            </div>
            <div className="conf-text">
              <p>This attachment will be deleted permanently. Are you sure?</p>
            </div>
            <footer className="actions">
              <Button
                className="cancel-btn"
                variant="secondary"
                onClick={this.closeConfirmationModal}
              >
                Cancel
              </Button>
              <Button className="upload-btn ok-btn" onClick={() => this.handleDeleteFile(file.id)}>
                Ok
              </Button>
            </footer>
          </ConfirmationModal>
        }
      </React.Fragment>
    );
  }
}

VoucherFileListItem.propTypes = {
  dealType: PropTypes.string,
  analyticProperties: PropTypes.shape({
    actionPrefix: PropTypes.string,
    categoryPrefix: PropTypes.string,
  }),
  voucher: PropTypes.shape({
    is_early_invoice: PropTypes.bool,
  }).isRequired,
  isAddButton: PropTypes.bool.isRequired,
  isDropDown: PropTypes.bool,
  showSparkleIcon: PropTypes.bool,
  deleteFile: PropTypes.func,
  fetchFilesDownloadLink: PropTypes.func.isRequired,
  file: PropTypes.shape({
    id: PropTypes.number,
    deal: PropTypes.number,
    document_name: PropTypes.string,
    title: PropTypes.string,
    file_type: PropTypes.string,
    file_size: PropTypes.number,
    created_by: PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      avatar: PropTypes.string,
      email: PropTypes.shape({
        work: PropTypes.string,
      }),
    }),
    created: PropTypes.string,
    modified: PropTypes.string,
    can_edit: PropTypes.bool,
    privacy: PropTypes.string,
    is_lease_document: PropTypes.bool,
    extraction_opted_in: PropTypes.bool,
  }).isRequired,
  updateFile: PropTypes.func,
  onRemoveFromVoucher: PropTypes.func,
  userData: PropTypes.shape({
    id: PropTypes.number,
  }),
};

VoucherFileListItem.defaultProps = {
  dealType: '',
  analyticProperties: ({
    actionPrefix: '',
    categoryPrefix: '',
  }),
  deleteFile: () => {},
  isDropDown: true,
  showSparkleIcon: false,
  updateFile: () => {},
  onRemoveFromVoucher: null,
  userData: {},
};

export default VoucherFileListItem;
